/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  AVATAR_SIZE_EXTRA_EXTRA_SMALL,
  Avatar,
  AvatarProps,
  DIALOG_SIZE_SM,
  DialogContentProps,
  DialogProps,
  NavigationItem,
  UserProfile,
} from '@squiz/sds';
import { useConsoleServiceStore, useDialogStore } from 'dxpConsoleCore-mf/State';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { MyAccountForm } from '../../Components/MyAccountForm/MyAccountForm';
import { DXP_CONSOLE_USER_FALLBACK_TEXT } from '../../Constants/App.constants';
import { DXP_MENU_USER_ACCOUNT } from '../../Constants/NavItems.constants';
import { CORE_STRINGS } from '../../Constants/Strings.constants';
import { EntityDxpUserMe } from '../../Services/console';

/**
 * React hook to configure actions for the user account menu.
 */
export function useAccountOptions(): Array<NavigationItem> {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { consoleUser }: { consoleUser: EntityDxpUserMe | null } = useConsoleServiceStore();
  const { updateActiveDialog } = useDialogStore();

  // Set up the basic user profile
  const userProfile: UserProfile = {
    email: ``,
    firstName: DXP_CONSOLE_USER_FALLBACK_TEXT,
    lastName: ``,
  };

  // Update profile with auth values
  if (consoleUser) {
    userProfile.firstName = consoleUser.givenName;
    userProfile.lastName = consoleUser.familyName;
  }

  const myAccountItem = DXP_MENU_USER_ACCOUNT.filter((item: NavigationItem) => {
    return item.key === `my-account`;
  });

  const signOutItem = DXP_MENU_USER_ACCOUNT.filter((item: NavigationItem) => {
    return item.key === `sign-out`;
  });

  myAccountItem[0].action = (): void => {
    const myAccountDialog: DialogProps = {
      dialogContent: {
        renderContent: (props: DialogContentProps): JSX.Element => <MyAccountForm {...props} />,
      },
      dialogSize: DIALOG_SIZE_SM,
      heading: t(CORE_STRINGS.myAccount),
      scrollOnOverflow: false,
      stateHandler: useDialogStore,
    };
    updateActiveDialog(myAccountDialog);
  };

  const avatarProps: AvatarProps = {
    avatarSize: AVATAR_SIZE_EXTRA_EXTRA_SMALL,
    showTooltip: false,
    user: userProfile,
  };

  myAccountItem[0].icon = <Avatar {...avatarProps} />;

  // Update the "Sign out" item
  signOutItem[0].action = (): void => {
    navigate({ pathname: `/logout` });
  };

  return [myAccountItem[0], signOutItem[0]];
}
